import React, { useState, useEffect } from 'react';
import {
    Modal,
    DatePicker,
    Card,
    Typography,
    Spin,
    message,
    Divider,
    Row,
    Col,
    Statistic,
    Alert
} from 'antd';
import Table from 'components/Common/Table';
import { DollarOutlined, PlusOutlined, MinusOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import * as adminactions from 'store/actions/AuthActions';

dayjs.extend(isSameOrBefore);

const { Title, Text } = Typography;

const SalaryPreviewModal = ({ visible, onCancel, employeeId, currentBasicSalary, dateOfJoining }) => {
    const [selectedMonth, setSelectedMonth] = useState(dayjs());
    const [loading, setLoading] = useState(false);
    const [salaryData, setSalaryData] = useState({
        basicSalary: 0,
        fullBasicSalary: 0,
        isProrated: false,
        workingDays: 0,
        totalDaysInMonth: 0,
        allowances: [],
        commissions: [],
        otherPayments: [],
        deductions: []
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        if (visible && employeeId) {
            loadSalaryBreakdown();
        }
    }, [visible, employeeId, selectedMonth]);

    const loadSalaryBreakdown = async () => {
        setLoading(true);
        try {
            const monthYear = selectedMonth.format('YYYY-MM');
            const selectedDate = dayjs(monthYear + '-01');

            // Load all salary components including basic salaries
            const [basicSalariesRes, allowancesRes, commissionsRes, otherPaymentsRes, deductionsRes] = await Promise.all([
                adminactions.loadBasicSalaries(employeeId),
                adminactions.loadAllowances(employeeId),
                adminactions.loadCommissions(employeeId),
                adminactions.loadOtherPayments(employeeId),
                adminactions.loadDeductions(employeeId)
            ]);

            // Find the applicable basic salary for the selected month
            // (effective_date on or before the last day of selected month, get the most recent one)
            const getApplicableBasicSalary = () => {
                if (!basicSalariesRes || !basicSalariesRes.results) return 0;

                // Use end of month to include salaries effective during the selected month
                const monthEnd = selectedDate.endOf('month');

                const applicableSalaries = basicSalariesRes.results.filter(salary => {
                    if (!salary.effective_date) return false;
                    const salaryDate = dayjs(salary.effective_date);
                    return salaryDate.isSameOrBefore(monthEnd, 'day');
                });

                if (applicableSalaries.length === 0) return 0;

                // Sort by effective_date descending to get the most recent applicable salary
                applicableSalaries.sort((a, b) =>
                    dayjs(b.effective_date).valueOf() - dayjs(a.effective_date).valueOf()
                );

                return parseFloat(applicableSalaries[0].basic_salary) || 0;
            };

            // Filter data for selected month
            const filterByMonth = (items) => {
                if (!items || !items.results) return [];
                return items.results.filter(item => {
                    if (!item.effective_date) return false;

                    const itemDate = dayjs(item.effective_date);

                    // Item is applicable if:
                    // - effective_date is on or before the selected month
                    // - end_date is null OR end_date is on or after the selected month
                    if (itemDate.isSameOrBefore(selectedDate, 'month')) {
                        if (item.end_date) {
                            const endDate = dayjs(item.end_date);
                            return selectedDate.isSameOrBefore(endDate, 'month');
                        }
                        return true; // No end date, applies indefinitely
                    }

                    return false;
                });
            };

            const applicableBasicSalary = getApplicableBasicSalary();
            const filteredAllowances = filterByMonth(allowancesRes);
            const filteredCommissions = filterByMonth(commissionsRes);
            const filteredOtherPayments = filterByMonth(otherPaymentsRes);
            const filteredDeductions = filterByMonth(deductionsRes);

            // Calculate prorated basic salary for joining month
            let proratedBasicSalary = applicableBasicSalary;
            let isProrated = false;
            let workingDays = 0;
            let totalDaysInMonth = 0;

            if (dateOfJoining) {
                const joiningDate = dayjs(dateOfJoining);
                const joiningMonth = joiningDate.startOf('month');
                const selectedMonthStart = selectedDate.startOf('month');

                // Check if selected month is the joining month
                if (joiningMonth.isSame(selectedMonthStart, 'month')) {
                    totalDaysInMonth = selectedDate.endOf('month').date();
                    // Days from joining date to end of month (inclusive)
                    workingDays = totalDaysInMonth - joiningDate.date() + 1;
                    proratedBasicSalary = (applicableBasicSalary / totalDaysInMonth) * workingDays;
                    isProrated = true;
                }
            }

            setSalaryData({
                basicSalary: proratedBasicSalary,
                fullBasicSalary: applicableBasicSalary,
                isProrated: isProrated,
                workingDays: workingDays,
                totalDaysInMonth: totalDaysInMonth,
                allowances: filteredAllowances,
                commissions: filteredCommissions,
                otherPayments: filteredOtherPayments,
                deductions: filteredDeductions
            });
        } catch (error) {
            console.error('Error loading salary breakdown:', error);
            message.error('Failed to load salary breakdown');
        } finally {
            setLoading(false);
        }
    };

    const calculateAllowanceAmount = (allowance) => {
        let amount;
        if (allowance.calculation_type === 'percentage') {
            // Percentage-based allowances are calculated on prorated basic salary,
            // so they're automatically prorated - no additional proration needed
            const basicSalary = parseFloat(salaryData.basicSalary || 0);
            amount = (basicSalary * parseFloat(allowance.amount)) / 100;
        } else {
            // Fixed amount allowances
            amount = parseFloat(allowance.amount || 0);

            // Apply proration only for fixed amounts when attendance-based and it's the joining month
            if (allowance.is_attendance_based && salaryData.isProrated && salaryData.totalDaysInMonth > 0) {
                amount = (amount * salaryData.workingDays) / salaryData.totalDaysInMonth;
            }
        }

        return amount;
    };

    const calculateTotalAllowances = () => {
        return salaryData.allowances.reduce((sum, allowance) => {
            return sum + calculateAllowanceAmount(allowance);
        }, 0);
    };

    const calculateTotalCommissions = () => {
        return salaryData.commissions.reduce((sum, commission) => {
            return sum + parseFloat(commission.commission_amount || 0);
        }, 0);
    };

    const calculateTotalOtherPayments = () => {
        return salaryData.otherPayments.reduce((sum, payment) => {
            return sum + parseFloat(payment.amount || 0);
        }, 0);
    };

    const calculateDeductionAmount = (deduction) => {
        let amount = parseFloat(deduction.amount || 0);

        // Apply proration if deduction is attendance-based and it's the joining month
        if (deduction.is_attendance_based && salaryData.isProrated && salaryData.totalDaysInMonth > 0) {
            amount = (amount * salaryData.workingDays) / salaryData.totalDaysInMonth;
        }

        return amount;
    };

    const calculateTotalDeductions = () => {
        return salaryData.deductions.reduce((sum, deduction) => {
            return sum + calculateDeductionAmount(deduction);
        }, 0);
    };

    const calculateGrossSalary = () => {
        const basic = parseFloat(salaryData.basicSalary || 0);
        const allowances = calculateTotalAllowances();
        const commissions = calculateTotalCommissions();
        const otherPayments = calculateTotalOtherPayments();
        return basic + allowances + commissions + otherPayments;
    };

    const calculateNetSalary = () => {
        return Math.round(calculateGrossSalary() - calculateTotalDeductions());
    };

    const allowanceColumns = [
        {
            title: 'Type',
            dataIndex: ['allowance_type', 'name'],
            key: 'allowance_type',
            render: (text) => text || '-'
        },
        {
            title: 'Description',
            dataIndex: 'allowance_name',
            key: 'allowance_name',
            render: (text) => text || '-'
        },
        {
            title: 'Calculation',
            key: 'calculation',
            render: (_, record) => {
                if (record.calculation_type === 'percentage') {
                    // Percentage-based is auto-prorated via basic salary
                    return `${record.amount}% of Basic`;
                }
                // Fixed amount - show proration indicator only for fixed + attendance-based
                let calcText = 'Fixed Amount';
                if (record.is_attendance_based && salaryData.isProrated) {
                    calcText += ' (Prorated)';
                }
                return calcText;
            }
        },
        {
            title: 'Amount',
            key: 'amount',
            align: 'right',
            render: (_, record) => `₹${calculateAllowanceAmount(record).toLocaleString()}`
        }
    ];

    const commissionColumns = [
        {
            title: 'Title',
            dataIndex: 'commission_title',
            key: 'commission_title'
        },
        {
            title: 'Amount',
            dataIndex: 'commission_amount',
            key: 'commission_amount',
            align: 'right',
            render: (amount) => `₹${parseFloat(amount).toLocaleString()}`
        }
    ];

    const otherPaymentColumns = [
        {
            title: 'Type',
            dataIndex: 'payment_type_display',
            key: 'payment_type_display'
        },
        {
            title: 'Title',
            dataIndex: 'title',
            key: 'title'
        },
        {
            title: 'Amount',
            dataIndex: 'amount',
            key: 'amount',
            align: 'right',
            render: (amount) => `₹${parseFloat(amount).toLocaleString()}`
        }
    ];

    const deductionColumns = [
        {
            title: 'Type',
            dataIndex: ['deduction_type_fk', 'name'],
            key: 'deduction_type',
            render: (text) => text || '-'
        },
        {
            title: 'Description',
            dataIndex: 'deduction_name',
            key: 'deduction_name',
            render: (text) => {
                if (!text) return '-';
                return text;
            }
        },
        {
            title: 'Calculation',
            key: 'calculation',
            render: (_, record) => {
                if (record.is_attendance_based && salaryData.isProrated) {
                    return 'Prorated';
                }
                return '-';
            }
        },
        {
            title: 'Amount',
            key: 'amount',
            align: 'right',
            render: (_, record) => `₹${calculateDeductionAmount(record).toLocaleString()}`
        }
    ];

    return (
        <Modal
            title="Salary Breakdown"
            open={visible}
            onCancel={onCancel}
            width={900}
            footer={null}
            style={{ top: 20 }}
        >
            <div style={{ marginBottom: 20 }}>
                <Text strong>Select Month: </Text>
                <DatePicker
                    picker="month"
                    value={selectedMonth}
                    onChange={(date) => setSelectedMonth(date)}
                    format="MMMM YYYY"
                    style={{ marginLeft: 10, width: 200 }}
                />
            </div>

            {dateOfJoining && selectedMonth.isBefore(dayjs(dateOfJoining), 'month') ? (
                <Alert
                    message="Employee Not Joined"
                    description={`The employee had not joined in ${selectedMonth.format('MMMM YYYY')}. Joining date is ${dayjs(dateOfJoining).format('MMMM D, YYYY')}.`}
                    type="warning"
                    showIcon
                    style={{ marginBottom: 20 }}
                />
            ) : (
            <Spin spinning={loading}>
                {/* Summary Cards */}
                <Row gutter={16} style={{ marginBottom: 20 }}>
                    <Col span={8}>
                        <Card>
                            <Statistic
                                title="Gross Salary"
                                value={calculateGrossSalary()}
                                precision={2}
                                prefix={<><PlusOutlined /> ₹</>}
                                valueStyle={{ color: '#3f8600' }}
                            />
                        </Card>
                    </Col>
                    <Col span={8}>
                        <Card>
                            <Statistic
                                title="Total Deductions"
                                value={calculateTotalDeductions()}
                                precision={2}
                                prefix={<><MinusOutlined /> ₹</>}
                                valueStyle={{ color: '#cf1322' }}
                            />
                        </Card>
                    </Col>
                    <Col span={8}>
                        <Card style={{ background: '#e6f7ff', borderColor: '#1890ff' }}>
                            <Statistic
                                title="Net Salary"
                                value={calculateNetSalary()}
                                precision={0}
                                prefix={<><DollarOutlined /> ₹</>}
                                valueStyle={{ color: '#1890ff', fontWeight: 'bold' }}
                            />
                        </Card>
                    </Col>
                </Row>

                {/* Basic Salary */}
                <Card size="small" style={{ marginBottom: 16 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div>
                            <Title level={5} style={{ margin: 0 }}>Basic Salary</Title>
                            {salaryData.isProrated && (
                                <Text type="secondary" style={{ fontSize: 12 }}>
                                    Prorated: {salaryData.workingDays} of {salaryData.totalDaysInMonth} days (Joining month)
                                </Text>
                            )}
                        </div>
                        <div style={{ textAlign: 'right' }}>
                            <Text strong style={{ fontSize: 16 }}>₹{parseFloat(salaryData.basicSalary).toLocaleString('en-IN', { maximumFractionDigits: 2 })}</Text>
                            {salaryData.isProrated && (
                                <div>
                                    <Text type="secondary" style={{ fontSize: 12 }}>
                                        Full: ₹{parseFloat(salaryData.fullBasicSalary).toLocaleString('en-IN')}
                                    </Text>
                                </div>
                            )}
                        </div>
                    </div>
                </Card>

                {/* Allowances */}
                {salaryData.allowances.length > 0 && (
                    <Card size="small" style={{ marginBottom: 16 }}>
                        <Title level={5}>Allowances</Title>
                        <Table
                            columns={allowanceColumns}
                            dataSource={salaryData.allowances}
                            rowKey="id"
                            pagination={false}
                            size="small"
                            summary={() => (
                                <Table.Summary.Row>
                                    <Table.Summary.Cell colSpan={3} align="right">
                                        <Text strong>Total Allowances:</Text>
                                    </Table.Summary.Cell>
                                    <Table.Summary.Cell align="right">
                                        <Text strong>₹{calculateTotalAllowances().toLocaleString()}</Text>
                                    </Table.Summary.Cell>
                                </Table.Summary.Row>
                            )}
                        />
                    </Card>
                )}

                {/* Commissions */}
                {salaryData.commissions.length > 0 && (
                    <Card size="small" style={{ marginBottom: 16 }}>
                        <Title level={5}>Commissions</Title>
                        <Table
                            columns={commissionColumns}
                            dataSource={salaryData.commissions}
                            rowKey="id"
                            pagination={false}
                            size="small"
                            summary={() => (
                                <Table.Summary.Row>
                                    <Table.Summary.Cell align="right">
                                        <Text strong>Total Commissions:</Text>
                                    </Table.Summary.Cell>
                                    <Table.Summary.Cell align="right">
                                        <Text strong>₹{calculateTotalCommissions().toLocaleString()}</Text>
                                    </Table.Summary.Cell>
                                </Table.Summary.Row>
                            )}
                        />
                    </Card>
                )}

                {/* Other Payments */}
                {salaryData.otherPayments.length > 0 && (
                    <Card size="small" style={{ marginBottom: 16 }}>
                        <Title level={5}>Other Payments</Title>
                        <Table
                            columns={otherPaymentColumns}
                            dataSource={salaryData.otherPayments}
                            rowKey="id"
                            pagination={false}
                            size="small"
                            summary={() => (
                                <Table.Summary.Row>
                                    <Table.Summary.Cell colSpan={2} align="right">
                                        <Text strong>Total Other Payments:</Text>
                                    </Table.Summary.Cell>
                                    <Table.Summary.Cell align="right">
                                        <Text strong>₹{calculateTotalOtherPayments().toLocaleString()}</Text>
                                    </Table.Summary.Cell>
                                </Table.Summary.Row>
                            )}
                        />
                    </Card>
                )}

                {/* Deductions */}
                {salaryData.deductions.length > 0 && (
                    <Card size="small" style={{ marginBottom: 16 }}>
                        <Title level={5}>Deductions</Title>
                        <Table
                            columns={deductionColumns}
                            dataSource={salaryData.deductions}
                            rowKey="id"
                            pagination={false}
                            size="small"
                            summary={() => (
                                <Table.Summary.Row>
                                    <Table.Summary.Cell colSpan={3} align="right">
                                        <Text strong>Total Deductions:</Text>
                                    </Table.Summary.Cell>
                                    <Table.Summary.Cell align="right">
                                        <Text strong>₹{calculateTotalDeductions().toLocaleString()}</Text>
                                    </Table.Summary.Cell>
                                </Table.Summary.Row>
                            )}
                        />
                    </Card>
                )}

                <Divider />

                {/* Final Summary */}
                <Card style={{ background: '#f0f5ff' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                        <Text>
                            Basic Salary{salaryData.isProrated && <Text type="secondary"> (Prorated)</Text>}:
                        </Text>
                        <Text>₹{parseFloat(salaryData.basicSalary).toLocaleString('en-IN', { maximumFractionDigits: 2 })}</Text>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                        <Text>Allowances:</Text>
                        <Text>₹{calculateTotalAllowances().toLocaleString()}</Text>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                        <Text>Commissions:</Text>
                        <Text>₹{calculateTotalCommissions().toLocaleString()}</Text>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                        <Text>Other Payments:</Text>
                        <Text>₹{calculateTotalOtherPayments().toLocaleString()}</Text>
                    </div>
                    <Divider style={{ margin: '8px 0' }} />
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                        <Text strong>Gross Salary:</Text>
                        <Text strong style={{ color: '#3f8600' }}>₹{calculateGrossSalary().toLocaleString()}</Text>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                        <Text strong style={{ color: '#cf1322' }}>Total Deductions:</Text>
                        <Text strong style={{ color: '#cf1322' }}>₹{calculateTotalDeductions().toLocaleString()}</Text>
                    </div>
                    <Divider style={{ margin: '8px 0' }} />
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Title level={4} style={{ margin: 0, color: '#1890ff' }}>Net Salary:</Title>
                        <Title level={4} style={{ margin: 0, color: '#1890ff' }}>₹{calculateNetSalary().toLocaleString()}</Title>
                    </div>
                </Card>
            </Spin>
            )}
        </Modal>
    );
};

export default SalaryPreviewModal;
